import { clearShareLinkParams } from '@/components/page-specific/catering/common/helpers/basket-link'
import { graphql } from '@/generated/gql'
import { useCateringTeamOrderEnabled } from '@/helpers/features/cateringTeamOrder'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { useTaskQueue } from '@/hooks/useTaskQueue'
import { useNotifications } from '@/store/notificationStore'
import { useMutation } from '@apollo/client'

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { match } from 'ts-pattern'

import {
  type SelectorBasket,
  type BasketSelectionConfig,
  useBasketSelectionState,
} from '../../store/basketSelectionStore'
import { useBasketSelectionActions } from '../../store/basketSelectionStore'
import { useCreateBasketMutation } from '../menu-page/create-basket-mutation'
import { useGetBasketQuery } from '../menu-page/get-basket-query'
import { useAuthenticatedUserBasketSelection } from './authenticated'
import { useUnauthenticatedUserBasketSelection } from './unauthenticated'

const basketsMatch = (a: Array<SelectorBasket>, b: Array<SelectorBasket>) => {
  if (a.length !== b.length) return false

  for (let index = 0; index < a.length; index += 1) {
    const left = a[index]
    const right = b[index]

    if (!right) return false
    if (left.id !== right.id) return false
    if (left.friendlyId !== right.friendlyId) return false
    if (left.customName !== right.customName) return false
    if (left.isCateringTeamOrder !== right.isCateringTeamOrder) return false
  }

  return true
}

const deleteBasketMutation = graphql(`
  mutation BasketSelection_DeleteBasket($basketId: ID!, $ordered: Boolean!) {
    deleteGmBasket(basketId: $basketId, ordered: $ordered) {
      id
    }
  }
`)

// Hook to handle basket selection logic with Zustand store
export const useBasketSelection = (
  config: BasketSelectionConfig,
  // Only one caller (useShoppingCart) may write the shared store config — with two writers,
  // diverging configs ping-pong setConfig forever. Consumers pass syncConfig: false.
  { syncConfig = true }: { syncConfig?: boolean } = {},
) => {
  const store = useBasketSelectionState()
  const createBasketMutation = useCreateBasketMutation()
  const [runDelete] = useMutation(deleteBasketMutation)
  const deleteTaskQueue = useTaskQueue({ shouldProcess: true })
  const { showError, showSuccess } = useNotifications()
  const useBasketSharingValidation = useFeatureFlag(SPLITS.USE_BASKET_SHARING_VALIDATION)
  const cateringTeamOrderEnabled = useCateringTeamOrderEnabled()

  // Add basket fetching by ID for when initialSelectedId is provided
  const {
    run: runGetBasket,
    loading: getBasketLoading,
    basket: fetchedBasket,
    called: getBasketCalled,
    error: getBasketError,
    outcome: getBasketOutcome,
  } = useGetBasketQuery()

  // Destructure all store actions at once to avoid multiple hook calls
  const {
    setConfig,
    setHasUsedInitialSelection,
    setJustCreated,
    setSelectedBasket,
    addDeletedBasket,
    removeDeletedBasket,
    setCreating,
    setLoading,
    setOrderedUnselectedBaskets,
    setAuthenticatedBaskets,
    setUnauthenticatedBaskets,
  } = useBasketSelectionActions()

  const previousSelectedBasketRef = useRef<SelectorBasket | null>(null)
  const createBasketErrorHandledRef = useRef(false)

  useEffect(() => {
    setCreating(false)
    if (!syncConfig) return
    const { selectedBasket, hasUsedInitialSelection } = store
    if (
      hasUsedInitialSelection &&
      config.initialSelectedId &&
      selectedBasket?.id !== config.initialSelectedId
    ) {
      setHasUsedInitialSelection(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Initialize store config - only update if config actually changed
  useEffect(() => {
    if (!syncConfig) return

    const currentConfig = store.config
    if (
      currentConfig.isAuthenticatedUser !== config.isAuthenticatedUser ||
      currentConfig.vendorId !== config.vendorId ||
      currentConfig.initialSelectedId !== config.initialSelectedId ||
      currentConfig.preventAutomaticSelection !== config.preventAutomaticSelection ||
      currentConfig.accountId !== config.accountId ||
      currentConfig.isCateringTeamOrderUrl !== config.isCateringTeamOrderUrl
    ) {
      // A share visit resolves its basket while the drafts list is vendor-suppressed; the list
      // coming back (vendorId unset → set) is not a vendor change, and wiping here would
      // deselect the just-resolved basket for good (PT-1709 UAT row 012).
      const listUnsuppressed =
        !currentConfig.vendorId &&
        Boolean(config.isCateringTeamOrderUrl && config.initialSelectedId) &&
        store.selectedBasket?.id === config.initialSelectedId
      if (currentConfig.vendorId !== config.vendorId && !listUnsuppressed) {
        setSelectedBasket(null)
        setAuthenticatedBaskets([])
        setUnauthenticatedBaskets([])
      }
      if (currentConfig.initialSelectedId !== config.initialSelectedId) {
        setHasUsedInitialSelection(false)
      }
      setConfig(config)
    }
  }, [
    syncConfig,
    config,
    setConfig,
    setSelectedBasket,
    setAuthenticatedBaskets,
    setUnauthenticatedBaskets,
    setHasUsedInitialSelection,
    store.config,
    store.selectedBasket?.id,
  ])

  const authenticatedBasketSelection = useAuthenticatedUserBasketSelection({
    vendorId: store.config.isAuthenticatedUser ? store.config.vendorId : undefined,
  })

  const { update: updateUnauthenticatedUserBasket, ...unauthenticatedUserBasketSelection } =
    useUnauthenticatedUserBasketSelection({
      vendorId: !store.config.isAuthenticatedUser ? store.config.vendorId : undefined,
    })

  const onDelete = store.config.isAuthenticatedUser
    ? authenticatedBasketSelection.onDelete
    : unauthenticatedUserBasketSelection.onDelete

  const onCreate = store.config.isAuthenticatedUser
    ? authenticatedBasketSelection.onCreate
    : unauthenticatedUserBasketSelection.onCreate

  const onUpdate = useCallback(
    (updateData: SelectorBasket) => {
      if (store.config.isAuthenticatedUser) return
      updateUnauthenticatedUserBasket(updateData)
    },
    [store.config.isAuthenticatedUser, updateUnauthenticatedUserBasket],
  )

  const baskets = store.config.isAuthenticatedUser
    ? store.authenticatedBaskets
    : store.unauthenticatedBaskets

  const basketsWithoutDeleted = useMemo(() => {
    return baskets.filter(
      (basket) =>
        !store.deletedBaskets.includes(basket.id) &&
        // Gate off = team ordering fully retired from the UI: existing group baskets are
        // hidden entirely (stack, auto-selection, initial selection, delete-fallback) rather
        // than degrading to a standard-draft appearance.
        (cateringTeamOrderEnabled || !basket.isCateringTeamOrder),
    )
  }, [store.deletedBaskets, baskets, cateringTeamOrderEnabled])

  const unselectedBaskets: Array<SelectorBasket> = useMemo(() => {
    if (store.loading) return []
    const filteredBaskets = basketsWithoutDeleted.filter(
      (basket) => basket.id !== store.selectedBasket?.id,
    )
    return filteredBaskets
  }, [basketsWithoutDeleted, store.selectedBasket?.id, store.loading])

  const handleSelectBasket = useCallback(
    (basketId: string | null) => {
      const basketData = basketId
        ? (basketsWithoutDeleted.find((basket) => basket.id === basketId) ?? { id: basketId })
        : null
      setJustCreated(false)
      setSelectedBasket(basketData)
    },
    [basketsWithoutDeleted, setJustCreated, setSelectedBasket],
  )

  const deleteBasket = useCallback(
    (basketId: string) => {
      if (store.deletedBaskets.includes(basketId)) return

      addDeletedBasket(basketId)

      if (store.selectedBasket?.id === basketId) {
        const nextAvailableBasket = basketsWithoutDeleted
          .filter((basket) => basket.id !== basketId)
          .pop()
        setSelectedBasket(nextAvailableBasket ?? null)
      }

      deleteTaskQueue.addTask(async () => {
        const result = await runDelete({ variables: { basketId, ordered: false } })
        if (result.errors) {
          removeDeletedBasket(basketId)
        } else {
          onDelete(basketId)
          showSuccess('Draft basket deleted.')
        }
      })
    },
    [
      store.deletedBaskets,
      store.selectedBasket?.id,
      basketsWithoutDeleted,
      addDeletedBasket,
      setSelectedBasket,
      deleteTaskQueue,
      runDelete,
      removeDeletedBasket,
      onDelete,
      showSuccess,
    ],
  )

  const createBasket = useCallback(
    async (
      input: Parameters<typeof createBasketMutation.run>[0] & {
        onBasketCreated?: () => void
      },
    ) => {
      if (createBasketMutation.loading || store.loading) return

      const { onBasketCreated, ...mutationInput } = input

      createBasketErrorHandledRef.current = false
      previousSelectedBasketRef.current = store.selectedBasket ?? null

      setCreating(true)
      setSelectedBasket(null)
      setJustCreated(true)

      try {
        const result = await createBasketMutation.run(mutationInput)
        createBasketErrorHandledRef.current = true

        if (result.data) {
          previousSelectedBasketRef.current = null
          const newBasket = {
            id: result.data.basket.id,
            friendlyId: result.data.basket.friendlyId,
            // Carried into selection state so a just-created group basket themes/copies
            // correctly as a collapsed row before (or without) the list refetch landing.
            isCateringTeamOrder: result.data.basket.isCateringTeamOrder,
          }
          setSelectedBasket(newBasket)
          onCreate(newBasket)
          setCreating(false)
          showSuccess('Draft basket created.')
          onBasketCreated?.()
        } else {
          setCreating(false)
          setJustCreated(false)
          setSelectedBasket(previousSelectedBasketRef.current ?? null)
          previousSelectedBasketRef.current = null
          showError('Unable to create basket. Please try again.')
        }
      } catch (error) {
        if (createBasketErrorHandledRef.current) return
        createBasketErrorHandledRef.current = true

        setCreating(false)
        setLoading(false)
        setJustCreated(false)
        setSelectedBasket(previousSelectedBasketRef.current ?? null)
        previousSelectedBasketRef.current = null

        const fallbackMessage = 'Unable to create basket. Please try again.'
        const errorMessage =
          error instanceof Error && error.message?.trim() ? error.message : fallbackMessage
        showError(errorMessage)
      }
    },
    [
      store.loading,
      store.selectedBasket,
      createBasketMutation,
      onCreate,
      setCreating,
      setSelectedBasket,
      setJustCreated,
      setLoading,
      showSuccess,
      showError,
    ],
  )

  const onBasketFetchSuccess = useCallback(
    (basketData: SelectorBasket) => {
      onUpdate(basketData)
    },
    [onUpdate],
  )

  const onBasketFetchError = useCallback(
    (basketId: string) => {
      onDelete(basketId)
    },
    [onDelete],
  )

  const setBasket = useCallback(
    (basketData: SelectorBasket | null) => {
      setSelectedBasket(basketData)
      setLoading(false)
    },
    [setSelectedBasket, setLoading],
  )

  const expectedAccountId = useMemo(() => {
    if (store.config.accountId === undefined || store.config.accountId === null) {
      return null
    }

    return String(store.config.accountId)
  }, [store.config.accountId])

  const initialSelectionCandidate = useMemo(() => {
    if (
      createBasketMutation.loading ||
      !store.config.initialSelectedId ||
      store.hasUsedInitialSelection ||
      basketsWithoutDeleted.length === 0
    ) {
      return null
    }

    return (
      basketsWithoutDeleted.find((basket) => basket.id === store.config.initialSelectedId) ?? null
    )
  }, [
    basketsWithoutDeleted,
    createBasketMutation.loading,
    store.config.initialSelectedId,
    store.hasUsedInitialSelection,
  ])

  useEffect(() => {
    if (!initialSelectionCandidate) return
    if (store.selectedBasket?.id === initialSelectionCandidate.id) {
      if (!store.hasUsedInitialSelection) {
        setHasUsedInitialSelection(true)
      }
      return
    }

    setBasket(initialSelectionCandidate)
    setHasUsedInitialSelection(true)
  }, [
    initialSelectionCandidate,
    setBasket,
    setHasUsedInitialSelection,
    store.hasUsedInitialSelection,
    store.selectedBasket?.id,
  ])

  const shouldFetchInitialBasket = useMemo(() => {
    if (
      !store.config.initialSelectedId ||
      store.hasUsedInitialSelection ||
      getBasketCalled ||
      getBasketLoading
    ) {
      return false
    }

    const basketsHaveInitialId = basketsWithoutDeleted.some(
      (basket) => basket.id === store.config.initialSelectedId,
    )

    // A team-order link always resolves by id: the basket is someone else's, so it can never
    // be in the viewer's own list — and that list is usually suppressed (empty) for these
    // visits, which would make the legacy shortcut below skip the fetch entirely. Gated on
    // cateringTeamOrder so flipping the flag off restores today's behavior exactly.
    if (
      !useBasketSharingValidation &&
      !(cateringTeamOrderEnabled && store.config.isCateringTeamOrderUrl)
    ) {
      return basketsWithoutDeleted.length > 0
        ? !basketsWithoutDeleted.some((basket) => basket.id === store.config.initialSelectedId)
        : false
    }

    return !basketsHaveInitialId
  }, [
    basketsWithoutDeleted,
    getBasketCalled,
    getBasketLoading,
    cateringTeamOrderEnabled,
    store.config.initialSelectedId,
    store.config.isCateringTeamOrderUrl,
    store.hasUsedInitialSelection,
    useBasketSharingValidation,
  ])

  useEffect(() => {
    if (!shouldFetchInitialBasket || !store.config.initialSelectedId) return
    runGetBasket(store.config.initialSelectedId)
  }, [shouldFetchInitialBasket, runGetBasket, store.config.initialSelectedId])

  const fetchedBasketResult = useMemo(() => {
    if (!store.config.initialSelectedId || store.hasUsedInitialSelection) {
      return { status: 'idle' } as const
    }

    if (!getBasketCalled || getBasketLoading) {
      return { status: 'loading' } as const
    }

    if (getBasketError || !fetchedBasket) {
      // Group share links arrive as typed codes (PT-1709); flag off keeps the legacy mapping.
      if (cateringTeamOrderEnabled && getBasketOutcome === 'forbidden') {
        return { status: 'account_mismatch' } as const
      }
      if (!store.config.isAuthenticatedUser) {
        return { status: 'requires_authentication' } as const
      }
      return { status: 'not_found' } as const
    }

    if (fetchedBasket.id !== store.config.initialSelectedId) {
      return { status: 'not_found' } as const
    }

    // Same retirement rule as basketsWithoutDeleted: with the flag off, a deep link to a
    // group basket resolves exactly like a basket that doesn't exist.
    if (!cateringTeamOrderEnabled && fetchedBasket.isCateringTeamOrder) {
      return { status: 'not_found' } as const
    }

    const fetchedAccountId =
      fetchedBasket.settings?.account?.id !== undefined &&
      fetchedBasket.settings?.account?.id !== null
        ? String(fetchedBasket.settings.account.id)
        : null

    if (
      store.config.isAuthenticatedUser &&
      expectedAccountId &&
      fetchedAccountId &&
      expectedAccountId !== fetchedAccountId
    ) {
      return { status: 'account_mismatch' } as const
    }

    if (!store.config.isAuthenticatedUser && fetchedBasket.settings?.isGuestBasket !== true) {
      return { status: 'requires_authentication' } as const
    }

    const basket: SelectorBasket = {
      id: fetchedBasket.id,
      friendlyId: fetchedBasket.friendlyId || undefined,
      customName: fetchedBasket.customName || undefined,
      // Same reason as createBasket's projection: without this, a deep-linked group
      // basket is treated as standard in the stack until the list refetch lands.
      isCateringTeamOrder: fetchedBasket.isCateringTeamOrder,
    }

    return { status: 'match', basket } as const
  }, [
    expectedAccountId,
    fetchedBasket,
    getBasketCalled,
    getBasketError,
    getBasketLoading,
    getBasketOutcome,
    cateringTeamOrderEnabled,
    store.config.initialSelectedId,
    store.config.isAuthenticatedUser,
    store.hasUsedInitialSelection,
  ])

  useEffect(() => {
    const cateringTeamOrderShareVisit =
      cateringTeamOrderEnabled && Boolean(store.config.isCateringTeamOrderUrl)
    match(fetchedBasketResult)
      .with({ status: 'not_found' }, () => {
        // Only a definite miss earns the "gone" copy; a failed fetch keeps the generic message.
        const isGone = cateringTeamOrderShareVisit && getBasketOutcome === 'gone'
        showError(
          isGone
            ? 'This basket no longer exists. It may have been placed, deleted, or expired.'
            : 'Basket not found.',
        )
        // Deselect like the account_mismatch/requires_authentication siblings, so a dead link
        // lands on the empty-basket state rather than a stale selection.
        setBasket(null)
        setHasUsedInitialSelection(true)
        if (isGone) clearShareLinkParams()
      })
      .with({ status: 'account_mismatch' }, () => {
        showError('This basket does not belong to your account.')
        setBasket(null)
        setHasUsedInitialSelection(true)
        if (cateringTeamOrderShareVisit) clearShareLinkParams()
      })
      .with({ status: 'requires_authentication' }, () => {
        showError('Please sign in to access this basket.')
        setBasket(null)
        setHasUsedInitialSelection(true)
      })
      .with({ status: 'match' }, ({ basket }) => {
        if (store.config.isAuthenticatedUser) {
          onCreate(basket)
        } else {
          onUpdate(basket)
        }

        setBasket(basket)
        setHasUsedInitialSelection(true)
      })
      .otherwise(() => {})
  }, [
    fetchedBasketResult,
    getBasketOutcome,
    cateringTeamOrderEnabled,
    onCreate,
    onUpdate,
    setBasket,
    setHasUsedInitialSelection,
    showError,
    store.config.isAuthenticatedUser,
    store.config.isCateringTeamOrderUrl,
  ])

  const autoSelectCandidate = useMemo(() => {
    if (
      createBasketMutation.loading ||
      store.config.initialSelectedId ||
      store.config.preventAutomaticSelection ||
      basketsWithoutDeleted.length === 0 ||
      (createBasketMutation.basket?.id &&
        store.selectedBasket?.id === createBasketMutation.basket.id)
    ) {
      return null
    }

    const currentBasketStillExists = store.selectedBasket?.id
      ? basketsWithoutDeleted.some((basket) => basket.id === store.selectedBasket?.id)
      : false

    if (currentBasketStillExists) {
      return null
    }

    return basketsWithoutDeleted.find((basket) => !store.deletedBaskets.includes(basket.id)) ?? null
  }, [
    basketsWithoutDeleted,
    createBasketMutation.basket?.id,
    createBasketMutation.loading,
    store.config.initialSelectedId,
    store.config.preventAutomaticSelection,
    store.deletedBaskets,
    store.selectedBasket?.id,
  ])

  useEffect(() => {
    if (!autoSelectCandidate) return
    setBasket(autoSelectCandidate)
  }, [autoSelectCandidate, setBasket])

  useEffect(() => {
    if (!store.selectedBasket?.id) return
    if (store.deletedBaskets.includes(store.selectedBasket.id)) {
      setBasket(null)
    }
  }, [store.deletedBaskets, store.selectedBasket?.id, setBasket])

  // Split streams flag changes live, so cateringTeamOrder can turn off mid-session: retire an
  // already-selected group basket too, not just the list entries basketsWithoutDeleted hides.
  // The raw list is consulted as well because some selection paths store a partial
  // ({ id }-only) basket that never carried isCateringTeamOrder.
  useEffect(() => {
    if (cateringTeamOrderEnabled) return
    const selected = store.selectedBasket
    if (!selected?.id) return
    const listEntry = baskets.find((basket) => basket.id === selected.id)
    if (selected.isCateringTeamOrder || listEntry?.isCateringTeamOrder) {
      setBasket(null)
    }
  }, [cateringTeamOrderEnabled, baskets, store.selectedBasket, setBasket])

  const shouldStopLoading = useMemo(() => {
    if (createBasketMutation.error) return true

    // List-suppressed sessions (vendor unset — e.g. team-order contributors) never run a list
    // source, so the deep-link fetch settling is their only terminal signal. List-carrying
    // sessions keep waiting for their list — their loading ends via terminal-status setBasket.
    if (
      !store.config.vendorId &&
      store.config.initialSelectedId &&
      getBasketCalled &&
      !getBasketLoading
    ) {
      return true
    }

    if (store.config.isAuthenticatedUser) {
      return authenticatedBasketSelection.called && !authenticatedBasketSelection.loading
    }

    return unauthenticatedUserBasketSelection.called
  }, [
    authenticatedBasketSelection.called,
    authenticatedBasketSelection.loading,
    createBasketMutation.error,
    getBasketCalled,
    getBasketLoading,
    store.config.initialSelectedId,
    store.config.isAuthenticatedUser,
    store.config.vendorId,
    unauthenticatedUserBasketSelection.called,
  ])

  useEffect(() => {
    if (shouldStopLoading) {
      setLoading(false)
    }
  }, [shouldStopLoading, setLoading])

  // Sync ordered unselected baskets only when content actually diverges
  useEffect(() => {
    const orderedBaskets = store.orderedUnselectedBaskets

    if (basketsMatch(orderedBaskets, unselectedBaskets)) {
      return
    }

    setOrderedUnselectedBaskets([...unselectedBaskets])
  }, [unselectedBaskets, setOrderedUnselectedBaskets, store.orderedUnselectedBaskets])

  return {
    unselectedBaskets: store.orderedUnselectedBaskets,
    loading: store.loading || store.creating,
    selectedBasket: store.selectedBasket,
    // getBasketCalled counts too: contributor sessions suppress the drafts list entirely, so
    // the deep-link fetch is their only selection source — without it, called never turns true
    // and the cart's isInitialLoad skeletons forever.
    called:
      authenticatedBasketSelection.called ||
      unauthenticatedUserBasketSelection.called ||
      getBasketCalled,
    justCreated: store.justCreated,
    onBasketFetchSuccess,
    onBasketFetchError,
    deleteBasket,
    createBasket,
    setSelectedBasket: handleSelectBasket,
  }
}
